import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    AreaChart, Area, PieChart, Pie,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    Cell,BarChart, Bar,LabelList
} from 'recharts';
import Loader from '../../loader/Loader';
import styles from './Dashboard.module.css';
import OrderLocationsMap from '../../components/map/OrderLocationsMap';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];
const BAR_BASE_HUE = 260;          // start near purple
const BAR_HUE_STEP = 25;           // each bar = +25° hue

// New state for filters and search
const initialFilters = {
    productSnap: {
        warehouse: '',
        category: '',
        subCategory: '',
        searchTerm: ''
    },
    search: {
        searchType: 'productName',
        searchValue: ''
    },
    mapUserFilter: ''
};

// Load saved filters from localStorage
const loadSavedFilters = () => {
    try {
        const savedFilters = localStorage.getItem('dashboardFilters');
        if (savedFilters) {
            const parsed = JSON.parse(savedFilters);
            // Merge with initialFilters to ensure all properties exist
            return {
                productSnap: {
                    ...initialFilters.productSnap,
                    warehouse: parsed.productSnap?.warehouse || '',
                    category: parsed.productSnap?.category || '',
                    subCategory: parsed.productSnap?.subCategory || '',
                    searchTerm: '' // Don't persist search term
                },
                search: {
                    ...initialFilters.search,
                    searchType: parsed.search?.searchType || 'productName',
                    searchValue: '' // Don't persist search value
                },
                mapUserFilter: parsed.mapUserFilter || ''
            };
        }
    } catch (error) {
        console.error('Error loading saved filters:', error);
    }
    return initialFilters;
};


const Dashboard = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filters, setFilters] = useState(loadSavedFilters());
    const [searchResults, setSearchResults] = useState(null);
    const [mapLocations, setMapLocations] = useState([]);
    const [mapUserFilter, setMapUserFilter] = useState(loadSavedFilters().mapUserFilter); // '' = all users
    const [productSnapView, setProductSnapView] = useState('chart'); // 'chart' or 'table'
    const [debouncedValue, setDebouncedValue] = useState(filters.productSnap.searchTerm);
    const [statistics, setStatistics] = useState({
        orders: {
            total: 0,
            committed: 0,
            issued: 0,
            returned: 0,
            partially_returned: 0,
            cancelled: 0,
            averageValue: 0,
            cancellationRate: 0,
            partialReturnRate: 0,
            returnRate: 0
        },
        products: {
            total: 0,
            availableProducts: 0,
            totalItems: 0, 
            availableItems: 0,    
            warehouseBreakdown: {},
            categoryBreakdown: {},
            departmentBreakdown: {},
            topProducts: [] ,
            productSnapData: [] 
        },
        users: {
            total: 0,
            active: 0,
            adminUsers: 0,
            regularUsers: 0
        },
        orderTrends: []
    });

    const [orderOverviewTimeRange, setOrderOverviewTimeRange] = useState('6');
    const [orderTrendsTimeRange, setOrderTrendsTimeRange] = useState('6');
    const [isLoading, setIsLoading] = useState(true);
    const [distributionViews, setDistributionViews] = useState({
        warehouse: 'quantity',
        category: 'quantity',
        department: 'quantity'
    });

    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(filters.productSnap.searchTerm);
        }, 400);

        return () => clearTimeout(handler);
    }, [filters.productSnap.searchTerm]);

    // קריאה לבקאנד רק כשה-debouncedValue משתנה
    useEffect(() => {
        if (!debouncedValue.trim()) return;

        const fetchData = async () => {
            try {
           const res = await axios.get(`${BACKEND_URL}/api/products/actions/search`, {
        params: {
            type: 'productName',
            value: debouncedValue
        }
    });
                console.log("🔍 Results:", res.data);
                // פה אתה מעדכן state עם התוצאות
            } catch (err) {
                console.error(err);
            }
        };

        fetchData();
    }, [debouncedValue]);



const fetchStatistics = useCallback(async () => {
    setIsLoading(true);
    try {
        const [statsRes, allUsersRes] = await Promise.all([
            
             axios.get(`${BACKEND_URL}/api/orders/getDashboardStatistics`, {
                params: { 
                    orderOverviewTimeRange,
                    orderTrendsTimeRange,
                    mapUserFilter 
                }
            }),
            axios.get(`${BACKEND_URL}/api/users/getusers`),
        ]);

        const statsData = statsRes.data;
        

        setWarehouses(statsData.warehouses || []);
        setCategories(statsData.categories || []);
        setAllProducts(statsData.allProducts || []);
        setAllUsers(allUsersRes.data || []);
        setMapLocations(statsData.mapLocations || []);

        setStatistics({
            orders: {
                total: statsData.orders?.total || 0,
                committed: statsData.orders?.committed || 0,
                issued: statsData.orders?.issued || 0,
                returned: statsData.orders?.returned || 0,
                partially_returned: statsData.orders?.partially_returned || 0,
                cancelled: statsData.orders?.cancelled || 0,
                averageValue: statsData.orders?.averageValue || 0,
                cancellationRate: statsData.orders?.cancellationRate || 0,
                partialReturnRate: statsData.orders?.partialReturnRate || 0,
                returnRate: statsData.orders?.returnRate || 0,
                unreturnedItems: statsData.orders?.unreturnedItems || [],
                groupedUnreturnedItems: statsData.orders?.groupedUnreturnedItems || {}
            },
            products: {
                total: statsData.products?.total || 0,
                availableProducts: statsData.products?.availableProducts || 0,
                totalItems: statsData.products?.totalItems || 0,
                availableItems: statsData.products?.availableItems || 0,
                warehouseBreakdown: statsData.products?.warehouseBreakdown || { quantity: {}, products: {} },
                categoryBreakdown: statsData.products?.categoryBreakdown || { quantity: {}, products: {} },
                departmentBreakdown: statsData.products?.departmentBreakdown || { quantity: {}, products: {} },
                topProducts: statsData.products?.topProducts || [],
                productSnapData: statsData.products?.productSnapData || []
            },
            users: {
                total: statsData.users?.total || 0,
                active: statsData.users?.active || 0,
                adminUsers: statsData.users?.adminUsers || 0,
                regularUsers: statsData.users?.regularUsers || 0
            },
            orderTrends: statsData.orderTrends || []
        });

    } catch (error) {
        console.error('❌ Error fetching statistics:', error);
        // גם במקרה של שגיאה, השאר את ה-initial state
    } finally {
        setIsLoading(false);
    }
}, [orderOverviewTimeRange, orderTrendsTimeRange, mapUserFilter]);



    // Filter ALL products for the Product Snap view
const getFilteredProductSnapData = useCallback(() => {
    // ✅ בדיקה מלאה יותר
    if (!statistics?.products?.productSnapData || !Array.isArray(statistics.products.productSnapData)) {
        return [];
    }
    
    let filtered = statistics.products.productSnapData;
    
    // Apply search term filter
    if (filters.productSnap.searchTerm) {
        const searchLower = filters.productSnap.searchTerm.toLowerCase();
        filtered = filtered.filter(p => 
            p.name && p.name.toLowerCase().includes(searchLower)
        );
    }
    
    // Apply warehouse filter
    if (filters.productSnap.warehouse) {
        filtered = filtered.filter(p => p.warehouse === filters.productSnap.warehouse);
    }
    
    // Apply category filter
    if (filters.productSnap.category) {
        filtered = filtered.filter(p => p.category === filters.productSnap.category);
    }
    
    // Apply subCategory filter
    if (filters.productSnap.subCategory) {
        filtered = filtered.filter(p => p.subCategory === filters.productSnap.subCategory);
    }
    
    return filtered;
}, [statistics?.products?.productSnapData, filters.productSnap]);

    const renderTrackedProductsChart = (data, title) => {
        const AVAILABLE_COLOR = '#4CAF50';
        const USED_COLOR = '#FF9800';
        const COMMITTED_COLOR = '#2196F3';

        const filteredData = getFilteredProductSnapData();
        const displayData = filteredData.slice(0, 15); // Show first 15 products
        const getStockStatus = (available, total) => {
            if (total === 0) return { status: 'good', color: '#4caf50', label: 'Good' };
            const percentage = (available / total) * 100;
            if (percentage <= 20) return { status: 'critical', color: '#f44336', label: 'Critical' };
            if (percentage <= 50) return { status: 'low', color: '#ff9800', label: 'Low Stock' };
            return { status: 'good', color: '#4caf50', label: 'Good' };
        };

        return (
            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <h3>{title}</h3>
                    <div className={styles.headerControls}>
                        <div className={styles.viewToggle}>
                            <button
                                className={`${styles.toggleButton} ${productSnapView === 'chart' ? styles.active : ''}`}
                                onClick={() => setProductSnapView('chart')}
                            >
                                📊 Chart
                            </button>
                            <button
                                className={`${styles.toggleButton} ${productSnapView === 'table' ? styles.active : ''}`}
                                onClick={() => setProductSnapView('table')}
                            >
                                📋 Table
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Filters Section */}
                <div className={styles.filtersContainer}>
                    <div className={styles.filterGroup}>
                        <label>Search Product:</label>
                        <input
                            type="text"
                            placeholder="Search by product name..."
                            value={filters.productSnap.searchTerm}
                            onChange={(e) => setFilters(prev => ({
                                ...prev,
                                productSnap: { ...prev.productSnap, searchTerm: e.target.value }
                            }))}
                            className={styles.searchInput}
                        />
                    </div>
                    <div className={styles.filterGroup}>
                        <label>Warehouse:</label>
                        <select
                            value={filters.productSnap.warehouse}
                            onChange={(e) => setFilters(prev => ({
                                ...prev,
                                productSnap: { ...prev.productSnap, warehouse: e.target.value }
                            }))}
                            className={styles.filterSelect}
                        >
                            <option value="">All Warehouses</option>
                            {warehouses.map(w => (
                                <option key={w._id} value={w._id}>{w.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <label>Category:</label>
                        <select
                            value={filters.productSnap.category}
                            onChange={(e) => setFilters(prev => ({
                                ...prev,
                                productSnap: { 
                                    ...prev.productSnap, 
                                    category: e.target.value,
                                    subCategory: '' // Reset subCategory when category changes
                                }
                            }))}
                            className={styles.filterSelect}
                        >
                            <option value="">All Categories</option>
                            {categories.map(c => (
                                <option key={c._id} value={c._id}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterGroup}>
                        <label>SubCategory:</label>
                        <select
                            value={filters.productSnap.subCategory}
                            onChange={(e) => setFilters(prev => ({
                                ...prev,
                                productSnap: { ...prev.productSnap, subCategory: e.target.value }
                            }))}
                            className={styles.filterSelect}
                            disabled={!filters.productSnap.category}
                        >
                            <option value="">All SubCategories</option>
                            {filters.productSnap.category && 
                                categories.find(c => c._id === filters.productSnap.category)?.subcategories?.map(sc => (
                                    <option key={sc._id} value={sc._id}>{sc.name}</option>
                                ))
                            }
                        </select>
                    </div>
                </div>

                {/* Search Summary */}
                {(filters.productSnap.searchTerm || filters.productSnap.warehouse || filters.productSnap.category || filters.productSnap.subCategory) && (
                    <div className={styles.searchSummary}>
                        <div className={styles.summaryHeader}>
                            <h4>📊 Filtered Results Summary</h4>
                        </div>
                        <div className={styles.summaryStats}>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Products Found:</span>
                                <strong className={styles.summaryValue}>{filteredData.length}</strong>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Total Items:</span>
                                <strong className={styles.summaryValue}>
                                    {filteredData.reduce((sum, p) => sum + p.quantity, 0)}
                                </strong>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Available Items:</span>
                                <strong className={styles.summaryValue} style={{ color: '#4CAF50' }}>
                                    {filteredData.reduce((sum, p) => sum + p.available, 0)}
                                </strong>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Issued:</span>
                                <strong className={styles.summaryValue} style={{ color: '#FF9800' }}>
                                    {filteredData.reduce((sum, p) => sum + (p.issued || 0), 0)}
                                </strong>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Committed:</span>
                                <strong className={styles.summaryValue} style={{ color: '#2196F3' }}>
                                    {filteredData.reduce((sum, p) => sum + (p.committed || 0), 0)}
                                </strong>
                            </div>
                            <div className={styles.summaryItem}>
                                <span className={styles.summaryLabel}>Missing Items:</span>
                                <strong className={styles.summaryValue} style={{ color: '#FF6B6B' }}>
                                    {filteredData.reduce((sum, p) => sum + (p.missingItems || 0), 0)}
                                </strong>
                            </div>
                        </div>
                        {filters.productSnap.searchTerm && (
                            <div className={styles.activeFilter}>
                                🔍 Search: "<strong>{filters.productSnap.searchTerm}</strong>"
                            </div>
                        )}
                        {filters.productSnap.warehouse && (
                            <div className={styles.activeFilter}>
                                📦 Warehouse: <strong>{warehouses.find(w => w._id === filters.productSnap.warehouse)?.name}</strong>
                            </div>
                        )}
                        {filters.productSnap.category && (
                            <div className={styles.activeFilter}>
                                🏷️ Category: <strong>{categories.find(c => c._id === filters.productSnap.category)?.name}</strong>
                            </div>
                        )}
                        {filters.productSnap.subCategory && (
                            <div className={styles.activeFilter}>
                                🏷️ SubCategory: <strong>
                                    {categories.find(c => c._id === filters.productSnap.category)?.subcategories?.find(sc => sc._id === filters.productSnap.subCategory)?.name}
                                </strong>
                            </div>
                        )}
                    </div>
                )}

                {filteredData.length === 0 ? (
                    <div className={styles.noData}>
                        No products match your filters.
                    </div>
                ) : productSnapView === 'chart' ? (
           
                  
                    <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={displayData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-15} textAnchor="end" height={80} />
                            <YAxis allowDecimals={false} />
                            <Tooltip content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    const availablePercent = data.quantity > 0 ? ((data.available / data.quantity) * 100).toFixed(1) : 0;
                                    const issuedPercent = data.quantity > 0 ? (((data.issued || 0) / data.quantity) * 100).toFixed(1) : 0;
                                    const committedPercent = data.quantity > 0 ? (((data.committed || 0) / data.quantity) * 100).toFixed(1) : 0;
                                    const missingPercent = data.quantity > 0 ? (((data.missingItems || 0) / data.quantity) * 100).toFixed(1) : 0;
                                    
                                    return (
                                        <div className={styles.customTooltip}>
                                            <p className={styles.tooltipTitle}>{data.name}</p>
                                            <p className={styles.tooltipSubtitle}>Code: {data.productCode}</p>
                                            <p><strong>Total Quantity:</strong> {data.quantity}</p>
                                            <hr style={{margin: '0.5rem 0', border: 'none', borderTop: '1px solid #ddd'}} />
                                            <p style={{color: AVAILABLE_COLOR}}><strong>Available:</strong> {data.available} ({availablePercent}%)</p>
                                            <p style={{color: USED_COLOR}}><strong>Issued:</strong> {data.issued || 0} ({issuedPercent}%)</p>
                                            <p style={{color: COMMITTED_COLOR}}><strong>Committed:</strong> {data.committed || 0} ({committedPercent}%)</p>
                                            <p style={{color: '#FF6B6B'}}><strong>Missing Items:</strong> {data.missingItems || 0} ({missingPercent}%)</p>
                                        </div>
                                    );
                                }
                                return null;
                            }} />
                            <Legend />
                            <Bar dataKey="available" fill={AVAILABLE_COLOR} name="Available" />
                            <Bar dataKey="issued" fill={USED_COLOR} name="Issued" />
                            <Bar dataKey="committed" fill={COMMITTED_COLOR} name="Committed" />
                            <Bar dataKey="missingItems" fill="#FF6B6B" name="Missing Items" />
                        </BarChart>
                    </ResponsiveContainer>
                    



                    
                ) : (
                    <div className={styles.productTableContainer}>
                        <table className={styles.productTable}>
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Total</th>
                                    <th>Available</th>
                                    <th>Issued</th>
                                    <th>Committed</th>
                                    <th>Missing Items</th>
                                    <th>Availability</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayData.map((product, idx) => {
                                    const issued = product.issued || 0;
                                    const committed = product.committed || 0;
                                    const missingItems = product.missingItems || 0;
                                    const availablePercent = product.quantity > 0 ? (product.available / product.quantity) * 100 : 0;
                                    const stockStatus = getStockStatus(product.available, product.quantity);

                                    return (
                                        <tr key={idx}>
                                            <td>{product.name}</td>
                                            <td>{product.quantity}</td>
                                            <td>{product.available}</td>
                                            <td>{issued}</td>
                                            <td>{committed}</td>
                                            <td>{missingItems}</td>
                                            <td>
                                                <div className={styles.availabilityCell}>
                                                    <div className={styles.progressBar}>
                                                        <div
                                                            className={styles.progressFill}
                                                            style={{ width: `${availablePercent}%`, backgroundColor: stockStatus.color }}
                                                        ></div>
                                                    </div>
                                                    <span style={{ color: stockStatus.color, fontWeight: '600' }}>
                                                        {availablePercent.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };



    const handleSearch = useCallback(async () => {
    const { searchType, searchValue } = filters.search;

    if (!searchValue.trim()) {
        setSearchResults(null);
        return;
    }

    const res = await axios.get(`${BACKEND_URL}/api/products/actions/search`, {
        params: {
            type: searchType,
            value: searchValue
        }
    });

    setSearchResults(res.data);

}, [filters.search]);
    const handleClearSearch = () => {
        setFilters(prev => ({
            ...prev,
            search: {
                ...prev.search,
                searchValue: ''
            }
        }));
        setSearchResults(null);
    };

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    // Save filters to localStorage whenever they change
    useEffect(() => {
        try {
            const filtersToSave = {
                productSnap: {
                    warehouse: filters.productSnap.warehouse,
                    category: filters.productSnap.category,
                    subCategory: filters.productSnap.subCategory
                    // Don't save searchTerm
                },
                search: {
                    searchType: filters.search.searchType
                    // Don't save searchValue
                },
                mapUserFilter: mapUserFilter
            };
            localStorage.setItem('dashboardFilters', JSON.stringify(filtersToSave));
        } catch (error) {
            console.error('Error saving filters:', error);
        }
    }, [filters.productSnap.warehouse, filters.productSnap.category, filters.productSnap.subCategory, filters.search.searchType, mapUserFilter]);

    if (isLoading) return <Loader />;

// Unused function - kept for potential future use
// eslint-disable-next-line no-unused-vars
const renderPieChart = (data, title, type) => {
    // ✨ FIX IS HERE: Add a defensive check.
    // If the data object itself is null/undefined, or if the specific view inside it doesn't exist,
    // we can't render the chart. Return null or a placeholder.
    if (!data || !data[distributionViews[type]]) {
        return (
            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <h3>{title}</h3>
                </div>
                <div className={styles.noData}>Loading data...</div>
            </div>
        );
    }
    
    // Convert the data object to an array for the Pie chart
    const chartData = Object.entries(data[distributionViews[type]]).map(([name, value]) => ({ name, value }));

    return (
        <div className={styles.chartCard}>
            <div className={styles.chartHeader}>
                <h3>{title}</h3>
                <div className={styles.viewToggle}>
                    <button
                        className={`${styles.toggleButton} ${distributionViews[type] === 'quantity' ? styles.active : ''}`}
                        onClick={() => setDistributionViews(prev => ({ ...prev, [type]: 'quantity' }))}
                    >
                        Items
                    </button>
                    <button
                        className={`${styles.toggleButton} ${distributionViews[type] === 'products' ? styles.active : ''}`}
                        onClick={() => setDistributionViews(prev => ({ ...prev, [type]: 'products' }))}
                    >
                        Products
                    </button>
                </div>
            </div>
            {/* ✨ Add a check for empty data to show a message instead of an empty chart */}
            {chartData.length === 0 ? (
                <div className={styles.noData}>No data available for this view.</div>
            ) : (
                <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                        <Pie
                            data={chartData} // Use the processed array
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                        >
                            {chartData.map((_, index) => (
                                <Cell key={`cell-dist-${type}-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};
    
// Dashboard.js
// eslint-disable-next-line no-unused-vars
const breakdownToSortedArray = (breakdown, view = 'quantity') => {
    // ✨ FIX: Add a guard clause. If the breakdown or the specific view within it is missing, return an empty array.
    if (!breakdown || !breakdown[view]) {
        return [];
    }
    return Object.entries(breakdown[view])
            .map(([name, value]) => ({ name, value }))
            .sort((a, b) => b.value - a.value);   // largest first
}

// Unused function - kept for potential future use
// eslint-disable-next-line no-unused-vars
const renderCategoryBarChart = (breakdown, title) => {
    const data = breakdownToSortedArray(breakdown, distributionViews.category); 
    const maxVal = data.length > 0 ? Math.max(...data.map(d => d.value)) : 0;
    const chartHeight = Math.max(300, data.length * 24);

    return (
        <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
            <h3>{title}</h3>
            <div className={styles.viewToggle}>
            <button
                className={`${styles.toggleButton} ${distributionViews.category === 'quantity' ? styles.active : ''}`}
                onClick={() => setDistributionViews(prev => ({ ...prev, category: 'quantity' }))}
            >
                Items
            </button>
            <button
                className={`${styles.toggleButton} ${distributionViews.category === 'products' ? styles.active : ''}`}
                onClick={() => setDistributionViews(prev => ({ ...prev, category: 'products' }))}
            >
                Products
            </button>
            </div>
        </div>

        {data.length === 0 ? (
            <div className={styles.noData}>No data available for this view.</div>
        ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 0, right: 30, left: 100, bottom: 0 }}
                    barGap={4}
                >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis type="number" hide />
                    <YAxis
                        type="category"
                        dataKey="name"
                        width={180}
                        tick={{ fontSize: 12, width: 170 }}
                        interval={0}
                    />
                    <Tooltip />
                    <Bar
                        dataKey="value"
                        isAnimationActive={true}
                        radius={[0, 6, 6, 0]}
                    >
                        <LabelList dataKey="value" position="right" style={{ fill: '#333' }} />
                        {data.map((entry, idx) => (
                            <Cell
                                key={`cell-${idx}`}
                                fill={entry.value === maxVal ? '#FF8042' : `hsl(${BAR_BASE_HUE + idx * BAR_HUE_STEP}, 70%, 55%)`}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        )}
        </div>
    );
};

    // Render Unified Search Card
    const renderUnifiedSearchCard = () => {
        const { searchType, searchValue } = filters.search;

        const statusBadgeClass = (status) => {
            const classes = {
                'committed': styles.statusCommitted,
                'issued': styles.statusIssued,
                'returned': styles.statusReturned,
                'partially_returned': styles.statusPartial,
                'cancelled': styles.statusCancelled
            };
            return classes[status] || '';
        };

        return (
            <div className={`${styles.chartCard} ${styles.searchCard}`}>
                <div className={styles.chartHeader}>
                    <h3>🔍 Find It</h3>
                </div>
                
                <div className={styles.searchContainer}>
                    <div className={styles.searchControls}>
                        <select
                            value={searchType}
                            onChange={(e) => setFilters(prev => ({
                                ...prev,
                                search: { ...prev.search, searchType: e.target.value, searchValue: '' }
                            }))}
                            className={styles.searchTypeSelect}
                        >
                            <option value="productName">Product Name</option>
                            <option value="productCode">Product Code</option>
                            <option value="serialNumber">Serial Number</option>
                            <option value="user">User</option>
                        </select>
                        
                        {searchType === 'user' ? (
                            <select
                                value={searchValue}
                                onChange={(e) => setFilters(prev => ({
                                    ...prev,
                                    search: { ...prev.search, searchValue: e.target.value }
                                }))}
                                className={styles.userSelect}
                            >
                                <option value="">Select a user...</option>
                                {allUsers.map(user => (
                                    <option key={user._id} value={user._id}>
                                        {user.name} ({user.email})
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                placeholder={`Search by ${searchType === 'productCode' ? 'product code' : searchType === 'productName' ? 'product name' : 'serial number'}...`}
                                value={searchValue}
                                onChange={(e) => setFilters(prev => ({
                                    ...prev,
                                    search: { ...prev.search, searchValue: e.target.value }
                                }))}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchValue) {
                                        handleSearch();
                                    }
                                }}
                                className={styles.searchInputLarge}
                            />
                        )}
                        
                        <button 
                            onClick={handleSearch}
                            className={styles.searchButton}
                            disabled={!searchValue}
                        >
                            Search
                        </button>
                        <button
                            onClick={handleClearSearch}
                            className={styles.clearButton}
                        >
                            Clear
                        </button>
                    </div>

                    <div className={styles.searchResults}>
                        {searchResults === null ? (
                            <div className={styles.searchPlaceholder}>
                                Enter a search term to find products, serial numbers, or user orders.
                            </div>
                        ) : (searchResults.type === 'user' && searchResults.orders.length === 0) || (searchResults.type === 'product' && searchResults.items.length === 0) ? (
                            <div className={styles.noResults}>No results found for the selected criteria.</div>
                        ) : (
                            <div className={styles.resultsList}>
                                {searchResults.type === 'product' && searchResults.items.map((result, idx) => (
                                    <div key={idx} className={styles.resultItem}>
                                        {result.product && (
                                            <>
                                                <div className={styles.resultHeader}>
                                                    <h4>{result.product.productName}</h4>
                                                    <span className={styles.productCode}>{result.product.productCode}</span>
                                                </div>
                                                {result.serialNumber && (
                                                    <div className={styles.serialInfo}>
                                                        Serial: <strong>{result.serialNumber}</strong>
                                                    </div>
                                                )}
                                                
                                                {/* Serial Number - Current Status View */}
                                                {result.serialNumber ? (
                                                    <div className={styles.serialCurrentStatus}>
                                                        {result.isAvailable ? (
                                                            <div className={styles.availableStatus}>
                                                                <div className={styles.statusIcon}>✅</div>
                                                                <div className={styles.statusDetails}>
                                                                    <h5>Available</h5>
                                                                    <p>This item is currently available in the warehouse</p>
                                                                </div>
                                                            </div>
                                                        ) : result.currentOrder ? (
                                                            <div className={styles.inUseStatus} data-status={result.currentOrder.orderStatus}>
                                                                <div className={styles.statusIcon}>📦</div>
                                                                <div className={styles.statusDetails}>
                                                                    <h5>Currently {result.currentOrder.orderStatus === 'issued' ? 'Issued' : result.currentOrder.orderStatus === 'partially_returned' ? 'Partially Returned' : result.currentOrder.orderStatus === 'committed' ? 'Committed' : result.currentOrder.orderStatus}</h5>
                                                                    <div className={styles.orderNameId}>
                                                                        <strong>{result.currentOrder.orderName || result.currentOrder.orderNumber}</strong>
                                                                        <span className={styles.orderId}>ID: {result.currentOrder.orderNumber}</span>
                                                                    </div>
                                                                    <div className={styles.orderQuickInfo}>
                                                                        <span className={`${styles.statusBadge} ${statusBadgeClass(result.currentOrder.orderStatus)}`}>
                                                                            {result.currentOrder.orderStatus}
                                                                        </span>
                                                                        <span className={styles.userInfo}>
                                                                            👤 {allUsers.find(u => u._id === result.currentOrder.requestedBy)?.name || 'Unknown'}
                                                                        </span>
                                                                        {result.currentOrder.destination && (
                                                                            <span className={styles.locationInfo}>
                                                                                📍 {result.currentOrder.destination}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className={styles.orderDates}>
                                                                        <span>From: {new Date(result.currentOrder.fromDate).toLocaleDateString()}</span>
                                                                        <span>Until: {new Date(result.currentOrder.untilDate).toLocaleDateString()}</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : (
                                                    /* Product Code/Name Search - Show orders */
                                                    <>
                                                        <div className={styles.productInfo}>
                                                            <span>Available: {result.product.quantityAvailable}</span>
                                                            <span>Total: {result.product.quantity}</span>
                                                        </div>
                                                        {result.orders && result.orders.length > 0 && (
                                                            <div className={styles.orderInfo}>
                                                                <strong>Found in {result.orders.length} order(s):</strong>
                                                                {result.orders.map((order, oidx) => {
                                                                    const user = allUsers.find(u => u._id === order.requestedBy);
                                                                    return (
                                                                        <div key={oidx} className={styles.orderRow}>
                                                                            <span className={styles.orderName} title={order.orderName || order.orderNumber}>
                                                                                {order.orderName || order.orderNumber}
                                                                            </span>
                                                                            <span className={styles.orderUserName}>
                                                                                <span style={{ visibility: user ? 'visible' : 'hidden' }}>👤&nbsp;</span>
                                                                                {user ? user.name : ''}
                                                                            </span>
                                                                            <span className={styles.destination}>
                                                                                <span style={{ visibility: order.destination ? 'visible' : 'hidden' }}>📍&nbsp;</span>
                                                                                {order.destination || ''}
                                                                            </span>
                                                                            <span className={`${styles.statusBadge} ${statusBadgeClass(order.orderStatus)}`}>
                                                                                {order.orderStatus}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                ))}
                                {searchResults.type === 'user' && (
                                    <>
                                        <div className={styles.userSummary}>
                                            <h4 className={styles.userSummaryTitle}>{searchResults.user.name}</h4>
                                            <p className={styles.totalOrders}>Total Orders: <strong>{searchResults.summary.total}</strong></p>
                                            <div className={styles.userStatGrid}>
                                                <div className={styles.statItem}><span>Committed:</span><strong>{searchResults.summary.committed}</strong></div>
                                                <div className={styles.statItem}><span>Issued:</span><strong>{searchResults.summary.issued}</strong></div>
                                                <div className={styles.statItem}><span>Returned:</span><strong>{searchResults.summary.returned}</strong></div>
                                                <div className={styles.statItem}><span>Partial:</span><strong>{searchResults.summary.partially_returned}</strong></div>
                                            </div>
                                        </div>
                                        {searchResults.orders.map((order, idx) => (
                                            <div key={idx} className={styles.resultItem}>
                                                <div className={styles.resultHeader}>
                                                    <h4>{order.orderName || order.orderNumber}</h4>
                                                    <span className={`${styles.statusBadge} ${statusBadgeClass(order.orderStatus)}`}>
                                                        {order.orderStatus}
                                                    </span>
                                                </div>
                                                <div className={styles.orderDates}>
                                                    <span>From: {new Date(order.fromDate).toLocaleDateString()}</span>
                                                    <span>Until: {new Date(order.untilDate).toLocaleDateString()}</span>
                                                    <span className={styles.destination}>📍 {order.destination}</span>
                                                </div>
                                                <div className={styles.productsList}>
                                                    <strong>Products:</strong>
                                                    {order.products.map((p, pidx) => {
                                                        const fullProduct = allProducts.find(prod => (prod._id === (p.product?._id || p.product)));
                                                        const productCode = fullProduct?.productCode || p.productCode || 'N/A';
                                                        const managedBySerial = fullProduct?.managedBySerial || false;
                                                        const serialNumbers = p.serialNumbers || [];
                                                        
                                                        return (
                                                            <div key={pidx} className={styles.productItem}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                                                                    <span>
                                                                        <strong>{p.productName || 'Unknown'}</strong> ({productCode}) - Qty: {p.quantity}
                                                                    </span>
                                                                </div>
                                                                {managedBySerial && serialNumbers.length > 0 && (
                                                                    <div style={{ marginTop: '0.5rem', paddingLeft: '1rem' }}>
                                                                        <strong style={{ fontSize: '0.85rem', color: '#666' }}>Serial Numbers:</strong>
                                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                                                                            {serialNumbers.map((serial, sidx) => (
                                                                                <span key={sidx} style={{ 
                                                                                    background: '#e3f2fd', 
                                                                                    padding: '0.15rem 0.5rem', 
                                                                                    borderRadius: '4px', 
                                                                                    fontSize: '0.8rem',
                                                                                    fontFamily: 'monospace',
                                                                                    color: '#1976d2',
                                                                                    fontWeight: '600'
                                                                                }}>
                                                                                    {serial}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };
    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Dashboard</h1>
            </div>
            <div className={styles.quickStats}>
                <div className={`${styles.statCard} ${styles.filtered}`}>
                    <div className={styles.cardHeader}>
                        <h3>Orders Overview</h3>
                        <select 
                            value={orderOverviewTimeRange}
                            onChange={(e) => setOrderOverviewTimeRange(e.target.value)}
                            className={styles.select}
                        >
                            <option value="1">Last Month</option>
                            <option value="3">Last 3 Months</option>
                            <option value="6">Last 6 Months</option>
                            <option value="12">Last 12 Months</option>
                            <option value="all">All Time</option>
                        </select>
                    </div>
                    <div className={styles.statGrid}>
                        <div className={`${styles.statItem} ${styles.totalOrders}`}>
                            <span>Total Orders</span>
                            <strong>{statistics.orders.total}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.committed}`}>
                            <span>Committed</span>
                            <strong>{statistics.orders.committed}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.issued}`}>
                            <span>Issued</span>
                            <strong>{statistics.orders.issued}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.returned}`}>
                            <span>Returns</span>
                            <strong>{statistics.orders.returned}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.averageValue}`}>
                            <span>Average Value</span>
                            <strong>${statistics.orders.averageValue.toFixed(2)}</strong>
                        </div>
                    </div>
                </div>

                <div className={styles.statCard}>
                    <h3>Inventory Status</h3>
                    <div className={styles.statGrid}>
                    <div className={`${styles.statItem} ${styles.totalProducts}`}>
                        <span>Total Products</span>
                        <strong>{statistics.products.total}</strong>
                    </div>
                    <div className={`${styles.statItem} ${styles.totalItems}`}>
                        <span>Total Items</span>
                        <strong>{statistics.products.totalItems}</strong>
                    </div>
                    <div className={`${styles.statItem} ${styles.availableProducts}`}>
                        <span>Available Products</span>
                        <strong>{statistics.products.availableProducts}</strong>
                    </div>
                    <div className={`${styles.statItem} ${styles.availableItems}`}>
                        <span>Available Items</span>
                        <strong>{statistics.products.availableItems}</strong>
                    </div>
                    </div>
                </div>

                <div className={`${styles.statCard} ${styles.filtered}`}>
                    <div className={styles.cardHeader}>
                        <h3>Partial Returns</h3>
                    </div>
                    <div className={styles.statGrid}>
                        <div className={`${styles.statItem} ${styles.partiallyReturned}`}>
                            <span>Partial Returns</span>
                            <strong>{statistics.orders.partially_returned}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.returnRate}`}>
                            <span>Partial Return Rate</span>
                            <strong>{statistics.orders.partialReturnRate.toFixed(1)}%</strong>
                        </div>
                    </div>
                    
                    {/* Users with Partial Returns */}
                                {statistics.orders.groupedUnreturnedItems && Object.keys(statistics.orders.groupedUnreturnedItems).length > 0 && (
                        <div className={styles.partialReturnUsers}>
                            <h4 className={styles.partialReturnTitle}>⚠️ Users with Unreturned Items</h4>
                            <div className={styles.userBadgesList}>
                                {Object.values(statistics.orders.groupedUnreturnedItems).map((userGroup, idx) => {
                                    // ✅ עכשיו uniqueOrders הוא Array ולא Set
                                    const orderCount = userGroup.uniqueOrders?.length || userGroup.items?.length || 0;
                                    return (
                                        <div key={idx} className={styles.userBadge}>
                                            <span className={styles.userBadgeName}>{userGroup.userName}</span>
                                            <span className={styles.userBadgeCount}>
                                                {orderCount} {orderCount === 1 ? 'order' : 'orders'}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* New Search Cards Section */}
            <div className={styles.searchCardsGrid}>
                {renderTrackedProductsChart(statistics.products.productSnapData, 'Products Snap')}
                {renderUnifiedSearchCard()}
            </div>

            <div className={styles.chartCard}>
                <div className={styles.chartHeader}>
                    <h3>Order Locations</h3>
                </div>
                <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ fontSize: '0.9rem', fontWeight: '500' }}>Filter by User:</label>
                    <select
                        value={mapUserFilter}
                        onChange={(e) => setMapUserFilter(e.target.value)}
                        className={styles.userFilterSelect}
                        style={{ width: '250px' }}
                    >
                        <option value="">All Users</option>
                        {allUsers.map(user => (
                            <option key={user._id} value={user._id}>
                                {user.name}
                            </option>
                        ))}
                    </select>
                </div>
                
                {/* Pin Color Legend */}
                <div style={{ 
                    marginBottom: '1rem', 
                    padding: '0.75rem', 
                    background: '#f5f5f5', 
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1.5rem',
                    fontSize: '0.85rem'
                }}>
                    <span style={{ fontWeight: '600', color: '#555' }}>Pin Colors:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            borderRadius: '50%', 
                            backgroundColor: '#1976d2',
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}></div>
                        <span>Committed</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            borderRadius: '50%', 
                            backgroundColor: '#7b1fa2',
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}></div>
                        <span>Issued</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                        <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            borderRadius: '50%', 
                            backgroundColor: '#f57c00',
                            border: '1px solid white',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}></div>
                        <span>Partially Returned</span>
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#777', marginLeft: 'auto' }}>
                        * Pins with multiple colors indicate mixed order statuses at that location
                    </span>
                </div>
                
                <OrderLocationsMap locations={mapLocations} />
            </div>

            {/* Combined Users Statistics and Order Trends */}
            <div className={styles.sectionHeader}>
                <h2>Order Trends & Users Statistics</h2>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '1.5rem' }}>
                <div className={styles.chartCard}>
                    <div className={styles.cardHeader}>
                        <h3>Order Trends</h3>
                        <select 
                            value={orderTrendsTimeRange}
                            onChange={(e) => setOrderTrendsTimeRange(e.target.value)}
                            className={styles.select}
                        >
                            <option value="3">Last 3 Months</option>
                            <option value="6">Last 6 Months</option>
                            <option value="12">Last 12 Months</option>
                        </select>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={statistics.orderTrends}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Area 
                                type="monotone"
                                dataKey="orders"
                                stroke="#8884d8"
                                fill="#8884d8"
                                name="Orders"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                <div className={styles.statCard}>
                    <h3>Users Stats</h3>
                    <div className={styles.statGrid}>
                        <div className={`${styles.statItem} ${styles.totalUsers}`}>
                            <span>Total Users</span>
                            <strong>{statistics.users.total}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.adminUsers}`}>
                            <span>Admin Users</span>
                            <strong>{statistics.users.adminUsers}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.regularUsers}`}>
                            <span>Regular Users</span>
                            <strong>{statistics.users.regularUsers}</strong>
                        </div>
                        <div className={`${styles.statItem} ${styles.activeUsers}`}>
                            <span>Active Users</span>
                            <strong>{statistics.users.active}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
